import { useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import Select from "react-select";
import { Input, Label, Spinner } from "reactstrap";
import useCounterpartItems from "../hooks/useCounterpartItems";
import useDataFlowOptions from "../hooks/useDataFlowOptions";
import {
    buildCounterpartTypeOptions,
    isEndpointCounterpartType,
} from "../helpers/flowForm";
import CreateEndpointModal from "./CreateEndpointModal";

const selectMenuProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    styles: {
        menuPortal: (base) => ({
            ...base,
            zIndex: 2000,
        }),
    },
};

const CounterpartPicker = ({
    wrapperAssetId,
    value = null,
    onChange,
    typeLabel,
    required = false,
    error,
    disabled = false,
    enabled = true,
    t,
}) => {
    const [selectedType, setSelectedType] = useState(value?.type || "");
    const [search, setSearch] = useState("");
    const [isCreateOpen, setIsCreateOpen] = useState(false);

    const { data: options = {}, isLoading: optionsAreLoading } =
        useDataFlowOptions({ t });
    const { items, isLoading: itemsAreLoading } = useCounterpartItems({
        type: selectedType,
        search,
        wrapperAssetId,
        enabled: enabled && Boolean(selectedType),
        t,
    });

    const typeOptions = useMemo(
        () =>
            buildCounterpartTypeOptions(options.endpoint_types).map((option) => ({
                ...option,
                label: t(option.label),
            })),
        [options.endpoint_types, t]
    );
    const selectedTypeOption =
        typeOptions.find((option) => option.value === selectedType) || null;
    const selectedTypeLabel = selectedTypeOption?.label || selectedType;
    const isEndpointType = isEndpointCounterpartType(selectedType);

    useEffect(() => {
        if (value?.type) {
            setSelectedType(value.type);
        }
    }, [value?.type]);

    const handleTypeChange = (type) => {
        setSelectedType(type);
        setSearch("");
        setIsCreateOpen(false);

        if (typeof onChange === "function") {
            onChange(null);
        }
    };

    const handleSelect = (item) => {
        if (typeof onChange === "function") {
            onChange(item);
        }
    };

    return (
        <div className="data-flow-v2__picker">
            <Label className="form-label">
                {typeLabel}
                {required ? <span className="text-danger"> *</span> : null}
            </Label>
            <Select
                classNamePrefix="react-select"
                options={typeOptions}
                value={selectedTypeOption}
                onChange={(option) => handleTypeChange(option?.value || "")}
                placeholder={t("Select...")}
                isDisabled={disabled}
                isClearable
                {...selectMenuProps}
            />

            {selectedType && (
                <div className="data-flow-v2__picker-search mt-2">
                    <i className="ri-search-line" aria-hidden="true" />
                    <Input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder={t("Search {{type}}...", {
                            type: selectedTypeLabel.toLowerCase(),
                        })}
                        disabled={disabled}
                    />
                </div>
            )}

            {selectedType && (
                <div className="data-flow-v2__picker-list mt-2">
                    {optionsAreLoading || itemsAreLoading ? (
                        <div className="data-flow-v2__picker-empty text-muted">
                            <Spinner size="sm" className="me-2" />
                            {t("Loading...")}
                        </div>
                    ) : items.length === 0 ? (
                        <div className="data-flow-v2__picker-empty text-muted">
                            {t("No counterparts found.")}
                        </div>
                    ) : (
                        items.map((item) => (
                            <button
                                key={`${item.kind}-${item.id}`}
                                type="button"
                                className={`data-flow-v2__picker-item${
                                    value?.id === item.id &&
                                    value?.kind === item.kind
                                        ? " data-flow-v2__picker-item--selected"
                                        : ""
                                }`}
                                onClick={() => handleSelect(item)}
                                disabled={disabled}
                            >
                                <div>
                                    <div>{item.name}</div>
                                    {item.identifier ? (
                                        <div className="text-muted">
                                            {item.identifier}
                                        </div>
                                    ) : null}
                                </div>
                            </button>
                        ))
                    )}

                    {isEndpointType && (
                        <button
                            type="button"
                            className="data-flow-v2__picker-item data-flow-v2__picker-create"
                            onClick={() => setIsCreateOpen(true)}
                            disabled={disabled}
                        >
                            <i className="mdi mdi-plus me-1" aria-hidden="true" />
                            {t("Create new {{type}}", {
                                type: selectedTypeLabel,
                            })}
                        </button>
                    )}
                </div>
            )}

            {value && (
                <div className="data-flow-v2__selected-chip mt-2">
                    <span>{value.name}</span>
                    <span className="text-muted">
                        {selectedTypeLabel || value.type}
                    </span>
                    <button
                        type="button"
                        className="data-flow-v2__icon-btn"
                        onClick={() => handleSelect(null)}
                        disabled={disabled}
                        aria-label={t("Delete")}
                    >
                        <i className="ri-close-line" aria-hidden="true" />
                    </button>
                </div>
            )}

            {error && <div className="invalid-feedback d-block">{error}</div>}

            <CreateEndpointModal
                isOpen={isCreateOpen}
                type={selectedType}
                typeLabel={selectedTypeLabel}
                onClose={() => setIsCreateOpen(false)}
                onCreated={handleSelect}
            />
        </div>
    );
};

export default withNamespaces()(CounterpartPicker);
